/**
 * Contact Data Validation Utilities
 *
 * Utilities for validating contact data in NIS2 reports.
 * Type definitions are imported from @/types/contact
 */

import type { ContactData } from "@/types/contact";

/**
 * Check if contact data is complete and valid
 *
 * Validates that all required fields are filled and valid:
 * - Legal representative (name, surname, valid email)
 * - Security officer (name, surname, valid email)
 * - Operational contact (name, surname, valid email)
 * - Both confirmation checkboxes are checked
 *
 * Used to determine if user has already completed the form (e.g., on page refresh)
 *
 * @param data - Contact data to validate
 * @returns True if all required fields are complete and valid, false otherwise
 */
export function isContactDataComplete(data: ContactData): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Check Reprezentant Legal
  if (
    !data.reprezentantLegal.nume.trim() ||
    !data.reprezentantLegal.prenume.trim() ||
    !data.reprezentantLegal.functie.trim() ||
    !data.reprezentantLegal.email.trim() ||
    !emailRegex.test(data.reprezentantLegal.email)
  ) {
    return false;
  }

  // Check Responsabil Securitate
  if (
    !data.responsabilSecuritate.nume.trim() ||
    !data.responsabilSecuritate.prenume.trim() ||
    !data.responsabilSecuritate.functie.trim() ||
    !data.responsabilSecuritate.email.trim() ||
    !emailRegex.test(data.responsabilSecuritate.email)
  ) {
    return false;
  }

  // Check Contact Operational
  if (
    !data.contactOperational.nume.trim() ||
    !data.contactOperational.prenume.trim() ||
    !data.contactOperational.functie.trim() ||
    !data.contactOperational.email.trim() ||
    !emailRegex.test(data.contactOperational.email)
  ) {
    return false;
  }

  return true;
}

export function isConfirmationsComplete(data: ContactData): boolean {
  return data.confirmaExactitate && data.acceptaObligatii;
}
